import { useEffect, useState } from "react";

// A tab runs the code it loaded. After a deploy (the nightly ships at 06:00) a
// tab left open keeps the old code, which can show what the new server sends
// without knowing how to save it: the Production Partner field, added one
// morning, did nothing in tabs opened the day before until they were reloaded.
// So notice a newer build and offer a reload. Never forced: an upload or an
// unsaved edit may be under way (and an upload already asks before leaving).

const EVERY_MS = 30 * 60 * 1000;   // while the tab sits open
const AFTER_MS = 5 * 60 * 1000;    // at most this often on coming back to it

// The build's entry script, e.g. /assets/index-LU1HCs1R.js. None on the dev
// server, which has no build to compare.
function entryOf(doc: Document): string | null {
  const s = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  const src = s?.getAttribute("src");
  return src ? new URL(src, window.location.origin).pathname : null;
}

export function UpdateNotice() {
  const [stale, setStale] = useState(false);
  const [later, setLater] = useState(false);

  useEffect(() => {
    const mine = entryOf(document);
    if (!mine) return;
    let live = true;
    let last = Date.now();
    const check = async () => {
      last = Date.now();
      try {
        const res = await fetch("/", { cache: "no-store", credentials: "same-origin" });
        if (!res.ok) return;
        const now = entryOf(new DOMParser().parseFromString(await res.text(), "text/html"));
        if (live && now && now !== mine) { setStale(true); setLater(false); }
      } catch { /* offline: the next check will tell */ }
    };
    const onBack = () => {
      if (document.visibilityState === "visible" && Date.now() - last > AFTER_MS) void check();
    };
    const timer = window.setInterval(check, EVERY_MS);
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    return () => {
      live = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onBack);
      window.removeEventListener("focus", onBack);
    };
  }, []);

  if (!stale || later) return null;
  return (
    <div className="update-notice" role="status">
      <span>Mockingbird has been updated. Reload to use the latest version.</span>
      <button type="button" className="update-reload" onClick={() => window.location.reload()}>Reload</button>
      <button type="button" className="update-later" onClick={() => setLater(true)}>Later</button>
    </div>
  );
}
